import { z } from 'zod';
import { AssetSchema } from '../shared/asset.schema';
import { GPT_IMAGE_25_QUALITIES, GPT_IMAGE_2_SIZES, GPT_IMAGE_QUALITIES } from './gpt-image';
import { IMAGE_RESOLUTIONS } from './image-resolution';
const GEMINI_WIDE_ASPECT_RATIOS = [
    '1:1',
    '1:4',
    '1:8',
    '2:3',
    '3:2',
    '3:4',
    '4:1',
    '4:3',
    '4:5',
    '5:4',
    '8:1',
    '9:16',
    '16:9',
    '21:9',
];
const GPT_IMAGE_SIZE_FIELD = z
    .enum(GPT_IMAGE_2_SIZES)
    .default('1024x1024')
    .optional()
    .describe('The size of the generated image');
const GPT_IMAGE_2_FIELDS = {
    size: GPT_IMAGE_SIZE_FIELD,
    quality: z
        .enum(GPT_IMAGE_QUALITIES)
        .default('auto')
        .optional()
        .describe('The rendering quality of the generated image'),
};
const GPT_IMAGE_25_FIELDS = {
    size: GPT_IMAGE_SIZE_FIELD,
    quality: z
        .enum(GPT_IMAGE_25_QUALITIES)
        .default('auto')
        .optional()
        .describe('The rendering quality of the generated image'),
};
const ImageGenerationWidgetFireflyV4InputSchema = z.object({
    model: z.literal('firefly-v4-standard').describe('The AI provider to use for image generation'),
    customModelId: z.string().optional().describe('The custom model ID to use for image generation'),
    size: z
        .enum(['2048x2048', '2304x1792', '1792x2304', '2688x1536', '1440x2560'])
        .default('2048x2048')
        .optional()
        .describe(`The size of the generated image`),
});
const ImageGenerationWidgetFireflyV3InputSchema = z.object({
    model: z.literal('firefly-v3').describe('The AI provider to use for image generation'),
    customModelId: z.string().optional().describe('The custom model ID to use for image generation'),
    size: z
        .enum([
        '2048x2048',
        '1024x1024',
        '2304x1792',
        '1792x2304',
        '2688x1536',
        '2688x1512',
        '1344x768',
        '1344x756',
        '1152x896',
        '896x1152',
    ])
        .default('2048x2048')
        .optional()
        .describe(`The size of the generated image`),
});
const ImageGenerationWidgetGemini31FlashInputSchema = z.object({
    model: z
        .literal('gemini-3.1-flash-image')
        .describe('The AI provider to use for image generation'),
    aspectRatio: z
        .enum(GEMINI_WIDE_ASPECT_RATIOS)
        .default('1:1')
        .optional()
        .describe('The aspect ratio of the generated image'),
    resolution: z
        .enum(['1K', '2K'])
        .optional()
        .describe('The output resolution tier of the generated image'),
});
const ImageGenerationWidgetGeminiNanoBanana21InputSchema = z.object({
    model: z
        .literal('gemini-nano-banana-2.1')
        .describe('The AI provider to use for image generation'),
    aspectRatio: z
        .enum(GEMINI_WIDE_ASPECT_RATIOS)
        .default('1:1')
        .optional()
        .describe('The aspect ratio of the generated image'),
    resolution: z
        .enum(IMAGE_RESOLUTIONS)
        .optional()
        .describe('The output resolution tier of the generated image'),
});
const ImageGenerationWidgetGemini3ProInputSchema = z.object({
    model: z.literal('gemini-3-pro-image').describe('The AI provider to use for image generation'),
    aspectRatio: z
        .enum(['1:1', '3:2', '2:3', '3:4', '4:3', '4:5', '5:4', '9:16', '16:9', '21:9'])
        .default('1:1')
        .optional()
        .describe('The aspect ratio of the generated image'),
    resolution: z
        .enum(IMAGE_RESOLUTIONS)
        .optional()
        .describe('The output resolution tier of the generated image'),
});
const ImageGenerationWidgetGPTImage1InputSchema = z.object({
    model: z.literal('gpt-image-1').describe('The AI provider to use for image generation'),
    size: z
        .enum(['1024x1024', '1536x1024', '1024x1536'])
        .default('1024x1024')
        .optional()
        .describe('The size of the generated image'),
    referenceAccuracy: z
        .enum(['low', 'high'])
        .optional()
        .describe('The accuracy of the reference image'),
});
const ImageGenerationWidgetGPTImage2InputSchema = z.object({
    model: z.literal('gpt-image-2').describe('The AI provider to use for image generation'),
    ...GPT_IMAGE_2_FIELDS,
});
const ImageGenerationWidgetGPTImage25SunburstInputSchema = z.object({
    model: z
        .literal('gpt-image-2.5-sunburst')
        .describe('The AI provider to use for image generation'),
    ...GPT_IMAGE_25_FIELDS,
});
const ImageGenerationWidgetGPTImage25FlareInputSchema = z.object({
    model: z.literal('gpt-image-2.5-flare').describe('The AI provider to use for image generation'),
    ...GPT_IMAGE_25_FIELDS,
});
const ImageGenerationWidgetDallE3InputSchema = z.object({
    model: z.literal('dall-e-3').describe('The AI provider to use for image generation'),
    size: z
        .enum(['1024x1024', '1792x1024', '1024x1792'])
        .default('1024x1024')
        .optional()
        .describe('The size of the generated image'),
});
// Their providers retired the models below, so nothing generates with them any more. Their
// literals stay in the union because saved widgets and persisted chat options still carry them,
// and dropping them would fail their parse on read. New generations are redirected by
// `normalizeImageGenerationInput`.
const ImageGenerationWidgetGPTImage15InputSchema = z.object({
    model: z.literal('gpt-image-1.5').describe('The AI provider to use for image generation'),
    ...GPT_IMAGE_2_FIELDS,
});
const ImageGenerationWidgetImageGen3Capability001InputSchema = z.object({
    model: z
        .literal('imagen-3.0-capability-001')
        .describe('The AI provider to use for image generation'),
    expansion: z
        .object({
        top: z.number(),
        left: z.number(),
        bottom: z.number(),
        right: z.number(),
    })
        .optional(),
    maskMode: z
        .enum(['INPAINT_INSERTION', 'INPAINT_REMOVAL', 'OUTPAINT', 'BGSWAP'])
        .default('OUTPAINT')
        .describe('The mode for applying the mask image, if provided'),
});
const ImageGenerationWidgetImageGen4InputSchema = z.object({
    model: z
        .literal('imagen-4.0-generate-001')
        .describe('The AI provider to use for image generation'),
    aspectRatio: z
        .enum(['1:1', '3:4', '4:3', '9:16', '16:9'])
        .default('1:1')
        .optional()
        .describe('The aspect ratio of the generated image'),
    resolution: z
        .enum(['1K', '2K'])
        .optional()
        .describe('The output resolution tier of the generated image'),
});
const ImageGenerationWidgetCustomWorkflowInputSchema = z.object({
    model: z.literal('custom-workflow').describe('Use a Custom workflow for image generation'),
    customWorkflowId: z.string().describe('The ID of the Custom workflow to execute'),
    customWorkflowInputs: z
        .record(z.string(), z.union([z.string(), z.number()]))
        .optional()
        .describe('Additional custom workflow inputs mapped by input name'),
});
const ImageGenerationWidgetInputObject = {
    prompt: z.string().describe('The text prompt describing the desired image'),
    variations: z.number().min(1).max(4).default(1).describe('Number of variations to generate'),
    settings: z
        .discriminatedUnion('model', [
        ImageGenerationWidgetGPTImage1InputSchema,
        ImageGenerationWidgetGPTImage2InputSchema,
        ImageGenerationWidgetGPTImage25SunburstInputSchema,
        ImageGenerationWidgetGPTImage25FlareInputSchema,
        ImageGenerationWidgetGPTImage15InputSchema,
        ImageGenerationWidgetDallE3InputSchema,
        ImageGenerationWidgetImageGen4InputSchema,
        ImageGenerationWidgetGemini31FlashInputSchema,
        ImageGenerationWidgetGeminiNanoBanana21InputSchema,
        ImageGenerationWidgetFireflyV3InputSchema,
        ImageGenerationWidgetFireflyV4InputSchema,
        ImageGenerationWidgetImageGen3Capability001InputSchema,
        ImageGenerationWidgetGemini3ProInputSchema,
        ImageGenerationWidgetCustomWorkflowInputSchema,
    ])
        .optional()
        .describe('Settings for image generation, including model and parameters')
        .default({ model: 'gemini-3-pro-image', aspectRatio: '1:1' }),
    mask: AssetSchema.optional().describe('Optional base64-encoded mask image for inpainting'),
};
export const ImageGenerationWidgetInputSchema = z.object(ImageGenerationWidgetInputObject);
export const DEPRECATED_IMAGE_MODEL_REPLACEMENTS = {
    'imagen-4.0-generate-001': 'gemini-3.1-flash-image',
    'imagen-3.0-capability-001': 'gemini-3.1-flash-image',
    'gpt-image-1.5': 'gpt-image-2',
};
/**
 * Redirects a retired model to its replacement before generation. Apply it to generation input
 * only, never to a saved widget being read back: that widget records which model actually
 * produced the image, and rewriting it would misattribute history.
 */
export function normalizeImageGenerationInput(input) {
    const replacement = DEPRECATED_IMAGE_MODEL_REPLACEMENTS[input.settings.model];
    if (!replacement) {
        return input;
    }
    return ImageGenerationWidgetInputSchema.parse({
        ...input,
        settings: { ...input.settings, model: replacement },
    });
}
export const ImageGenerationWidgetSchemaKey = 'image-generation-widget:v1';
export const ImageGenerationWidgetSchemaKind = 'image';
/** Server-owned key prefix for saved image-generation outputs. */
export const SAVED_IMAGE_ARTIFACT_KEY_PREFIX = 'saved-image:';
export const ImageGenerationWidgetSchema = z.object({
    version: z.literal(2).describe('Version of the image generation widget schema'),
    title: z.string().describe('Title of the image widget'),
    input: z.object({
        ...ImageGenerationWidgetInputObject,
        files: z.array(AssetSchema).describe('The input image assets, if any').optional(),
    }),
    images: z.array(AssetSchema).describe('The generated image assets'),
    createdAt: z.date().describe('Timestamp when the image was initially generated'),
});
